import { supabase } from '@/lib/supabase.js'
import { generateRoomCode } from './helpers.js'

export async function createRoom() {
  const roomId = generateRoomCode()
  const { data, error } = await supabase
    .from('rooms')
    .insert({ id: roomId, status: 'waiting', created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}

export async function getRoom(roomId) {
  const { data, error } = await supabase.from('rooms').select('*').eq('id', roomId).single()
  return { data, error }
}

export async function updateRoomStatus(roomId, status) {
  const { data, error } = await supabase
    .from('rooms')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', roomId)
    .select()
    .single()
  return { data, error }
}

/**
 * Permanently delete a room and all its data (chess_moves + room_players
 * cascade automatically via ON DELETE CASCADE in the schema).
 * Called when the creator explicitly closes the room or leaves.
 */
export async function deleteRoom(roomId) {
  const { error } = await supabase.from('rooms').delete().eq('id', roomId)
  return { error }
}

// Keep closeRoom as a soft alias — RoomPage calls this, then deleteRoom
export async function closeRoom(roomId) {
  return deleteRoom(roomId)
}

/**
 * Upsert a player record — safe to call multiple times (StrictMode).
 */
export async function addRoomPlayer(roomId, role, sessionId) {
  const { data, error } = await supabase
    .from('room_players')
    .upsert(
      { room_id: roomId, role, peer_id: sessionId, joined_at: new Date().toISOString() },
      { onConflict: 'room_id,role', ignoreDuplicates: false }
    )
    .select()
    .single()
  return { data, error }
}

export async function removeRoomPlayer(roomId, role) {
  const { error } = await supabase.from('room_players').delete().eq('room_id', roomId).eq('role', role)
  return { error }
}

export async function getRoomPlayers(roomId) {
  const { data, error } = await supabase.from('room_players').select('*').eq('room_id', roomId)
  return { data, error }
}

export async function saveChessMove(roomId, { moveNumber, color, san, fen, from, to, promotion = null }) {
  const { data, error } = await supabase
    .from('chess_moves')
    .insert({ room_id: roomId, move_number: moveNumber, color, san, fen, from_square: from, to_square: to, promotion, created_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}

export async function getChessMoves(roomId) {
  const { data, error } = await supabase
    .from('chess_moves')
    .select('*')
    .eq('room_id', roomId)
    .order('move_number', { ascending: true })
  return { data, error }
}

export async function clearChessMoves(roomId) {
  const { error } = await supabase.from('chess_moves').delete().eq('room_id', roomId)
  return { error }
}

export async function updateRoomGame(roomId, fields) {
  const { data, error } = await supabase
    .from('rooms')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', roomId)
    .select()
    .single()
  return { data, error }
}